<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import TicketsAPI from 'dashboard/api/tickets';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DepartmentMailSettings from './DepartmentMailSettings.vue';

const route = useRoute();

const validSections = new Set([
  'general',
  'departments',
  'incoming',
  'outgoing',
  'integrations',
  'statuses',
  'categories',
]);

const currentSection = () => {
  const section = route.params.section?.toString();

  return validSections.has(section) ? section : 'general';
};

const activeTab = ref(currentSection());

watch(
  () => route.params.section,
  () => {
    activeTab.value = currentSection();
  }
);

const loading = ref(true);
const savingConfiguration = ref(false);
const savingStatus = ref(false);
const savingCategory = ref(false);

const statuses = ref([]);
const categories = ref([]);
const integrations = ref([]);

const statusDraft = ref(null);
const categoryDraft = ref(null);

const configuration = ref({
  prefix: 'TKT',
  manual_creation_enabled: true,
  api_creation_enabled: true,
  conversation_import_enabled: true,
  private_notes_enabled: true,
  public_reply_transport: 'email',
  default_priority: null,
  notify_requester_on_create: false,
  notify_requester_on_status_change: false,
});

const priorities = [
  {
    value: '',
    label: 'Sem prioridade padrão',
  },
  {
    value: 'low',
    label: 'Baixa',
  },
  {
    value: 'medium',
    label: 'Média',
  },
  {
    value: 'high',
    label: 'Alta',
  },
  {
    value: 'urgent',
    label: 'Urgente',
  },
];

const stateGroups = [
  {
    value: 'open',
    label: 'Aberto',
  },
  {
    value: 'waiting',
    label: 'Aguardando',
  },
  {
    value: 'resolved',
    label: 'Resolvido',
  },
  {
    value: 'closed',
    label: 'Fechado',
  },
];

const rootCategories = computed(() => {
  return categories.value.filter(item => !item.parent_id);
});

const integrationIcon = key => {
  const icons = {
    api: 'i-lucide-braces',
    conversation_import: 'i-lucide-message-square-share',
    email: 'i-lucide-mail',
    webhook: 'i-lucide-webhook',
  };

  return icons[key] || 'i-lucide-plug';
};

const integrationDirection = direction => {
  const names = {
    inbound: 'Entrada',
    outbound: 'Saída',
    bidirectional: 'Entrada e saída',
  };

  return names[direction] || direction;
};

const integrationStatus = status => {
  const names = {
    ready: 'Pronto',
    not_configured: 'Não configurado',
    disabled: 'Desativado',
    error: 'Erro',
  };

  return names[status] || status;
};

const stateGroupLabel = value => {
  return stateGroups.find(item => item.value === value)?.label || value;
};

const childrenOf = parentId => {
  return categories.value.filter(
    item => Number(item.parent_id) === Number(parentId)
  );
};

const loadConfiguration = async () => {
  const response = await TicketsAPI.configuration();

  configuration.value = {
    ...configuration.value,
    ...response.data,
  };
};

const loadStatuses = async () => {
  const response = await TicketsAPI.statuses();

  statuses.value = response.data?.payload || [];
};

const loadCategories = async () => {
  const response = await TicketsAPI.categories();

  categories.value = response.data?.payload || [];
};

const loadIntegrations = async () => {
  const response = await TicketsAPI.integrations();

  integrations.value = response.data?.payload || [];
};

const loadAll = async () => {
  loading.value = true;

  try {
    await Promise.all([
      loadConfiguration(),
      loadStatuses(),
      loadCategories(),
      loadIntegrations(),
    ]);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível carregar a Central de Tickets.'
    );
  } finally {
    loading.value = false;
  }
};

const saveConfiguration = async () => {
  savingConfiguration.value = true;

  try {
    const response = await TicketsAPI.updateConfiguration({
      prefix: configuration.value.prefix,

      manual_creation_enabled: configuration.value.manual_creation_enabled,

      api_creation_enabled: configuration.value.api_creation_enabled,

      conversation_import_enabled:
        configuration.value.conversation_import_enabled,

      private_notes_enabled: configuration.value.private_notes_enabled,

      public_reply_transport: configuration.value.public_reply_transport,

      default_priority: configuration.value.default_priority || null,

      notify_requester_on_create:
        configuration.value.notify_requester_on_create,

      notify_requester_on_status_change:
        configuration.value.notify_requester_on_status_change,
    });

    configuration.value = {
      ...configuration.value,
      ...response.data,
    };

    useAlert('Configurações atualizadas.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível salvar as configurações.'
    );
  } finally {
    savingConfiguration.value = false;
  }
};

const newStatus = () => {
  statusDraft.value = {
    id: null,
    name: '',
    slug: '',
    state_group: 'open',
    color: '#7C3AED',
    position: (statuses.value.length + 1) * 10,
    default_status: false,
    terminal: false,
    active: true,
  };
};

const editStatus = status => {
  statusDraft.value = {
    ...status,
  };
};

const saveStatus = async () => {
  const draft = statusDraft.value;

  if (!draft?.name?.trim()) {
    useAlert('Informe o nome do status.');
    return;
  }

  savingStatus.value = true;

  try {
    const payload = {
      name: draft.name.trim(),
      slug: draft.slug?.trim() || undefined,
      state_group: draft.state_group,
      color: draft.color || null,
      position: Number(draft.position || 0),
      default_status: Boolean(draft.default_status),
      terminal: Boolean(draft.terminal),
      active: Boolean(draft.active),
    };

    if (draft.id) {
      await TicketsAPI.updateStatus(draft.id, payload);
    } else {
      await TicketsAPI.createStatus(payload);
    }

    await loadStatuses();

    statusDraft.value = null;

    useAlert('Status salvo.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível salvar o status.'
    );
  } finally {
    savingStatus.value = false;
  }
};

const deleteStatus = async status => {
  if (!window.confirm(`Excluir o status "${status.name}"?`)) {
    return;
  }

  try {
    await TicketsAPI.deleteStatus(status.id);

    await loadStatuses();

    statusDraft.value = null;

    useAlert('Status excluído.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível excluir o status.'
    );
  }
};

const newCategory = () => {
  categoryDraft.value = {
    id: null,
    name: '',
    slug: '',
    color: '#7C3AED',
    position: (categories.value.length + 1) * 10,
    parent_id: null,
    active: true,
  };
};

const editCategory = category => {
  categoryDraft.value = {
    ...category,
  };
};

const saveCategory = async () => {
  const draft = categoryDraft.value;

  if (!draft?.name?.trim()) {
    useAlert('Informe o nome da categoria.');
    return;
  }

  savingCategory.value = true;

  try {
    const payload = {
      name: draft.name.trim(),
      slug: draft.slug?.trim() || undefined,
      color: draft.color || null,
      position: Number(draft.position || 0),
      parent_id: draft.parent_id ? Number(draft.parent_id) : null,
      active: Boolean(draft.active),
    };

    if (draft.id) {
      await TicketsAPI.updateCategory(draft.id, payload);
    } else {
      await TicketsAPI.createCategory(payload);
    }

    await loadCategories();

    categoryDraft.value = null;

    useAlert('Categoria salva.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível salvar a categoria.'
    );
  } finally {
    savingCategory.value = false;
  }
};

const deleteCategory = async category => {
  if (!window.confirm(`Excluir a categoria "${category.name}"?`)) {
    return;
  }

  try {
    await TicketsAPI.deleteCategory(category.id);

    await loadCategories();

    categoryDraft.value = null;

    useAlert('Categoria excluída.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível excluir a categoria.'
    );
  }
};

onMounted(loadAll);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex h-full min-h-0 flex-col bg-n-surface-1">
    <header class="border-b border-n-weak bg-n-solid-1 px-8 py-6">
      <div
        class="mx-auto flex w-full max-w-7xl items-start justify-between gap-5"
      >
        <div>
          <div class="flex items-center gap-3">
            <span
              class="inline-flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-ticket-check size-5" />
            </span>

            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-brand"
              >
                JOV Tickets
              </p>

              <h1 class="text-xl font-semibold text-n-slate-12">
                Central de Configurações
              </h1>
            </div>
          </div>

          <p class="mt-3 max-w-2xl text-sm text-n-slate-10">
            Configure como as informações sobem para Tickets e como elas descem
            para serviços externos.
          </p>
        </div>

        <button
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          @click="loadAll"
        >
          <span class="i-lucide-refresh-cw size-4" />
          Atualizar
        </button>
      </div>
    </header>

    <div v-if="loading" class="flex flex-1 items-center justify-center">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else
      class="mx-auto min-h-0 w-full max-w-7xl flex-1 overflow-y-auto px-8 py-6"
    >
      <main class="min-w-0 pb-20">
        <!-- GERAL -->
        <section v-if="activeTab === 'general'" class="max-w-4xl">
          <h2 class="text-lg font-semibold text-n-slate-12">Geral</h2>

          <p class="mt-1 text-sm text-n-slate-10">
            Definições principais da Central para esta empresa.
          </p>

          <div class="mt-6 rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <label class="text-sm font-medium text-n-slate-12"> Prefixo </label>

            <input
              v-model="configuration.prefix"
              maxlength="8"
              class="mt-2 block h-10 w-52 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm uppercase text-n-slate-12"
            />

            <p class="mt-2 text-xs text-n-slate-9">
              Exemplo:
              {{ configuration.prefix || 'TKT' }}-000001
            </p>
          </div>

          <div class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <label class="text-sm font-medium text-n-slate-12">
              Prioridade padrão
            </label>

            <select
              v-model="configuration.default_priority"
              class="mt-2 block h-10 w-full max-w-sm rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            >
              <option
                v-for="option in priorities"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <button
            type="button"
            class="mt-6 inline-flex h-10 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white disabled:opacity-50"
            :disabled="savingConfiguration"
            @click="saveConfiguration"
          >
            <Spinner v-if="savingConfiguration" class="text-white" />

            <span v-else class="i-lucide-save size-4" />

            Salvar
          </button>
        </section>

        <!-- ENTRADA -->
        <section v-else-if="activeTab === 'incoming'" class="max-w-4xl">
          <h2 class="text-lg font-semibold text-n-slate-12">
            Entrada de informações
          </h2>

          <p class="mt-1 text-sm text-n-slate-10">
            Defina quais fontes podem subir informações para a Central de
            Tickets.
          </p>

          <div class="mt-6 space-y-3">
            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Criação manual
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Usuários autorizados podem abrir Tickets diretamente no
                  painel.
                </p>
              </div>

              <input
                v-model="configuration.manual_creation_enabled"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>

            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  API JOV Tickets
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Permite que sistemas externos criem Tickets pela API.
                </p>
              </div>

              <input
                v-model="configuration.api_creation_enabled"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>

            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Importação de atendimento
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Copia o histórico de um atendimento para um Ticket
                  independente.
                </p>
              </div>

              <input
                v-model="configuration.conversation_import_enabled"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>
          </div>

          <button
            type="button"
            class="mt-6 inline-flex h-10 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white"
            @click="saveConfiguration"
          >
            <span class="i-lucide-save size-4" />
            Salvar entrada
          </button>
        </section>

        <!-- SAIDA -->
        <section v-else-if="activeTab === 'outgoing'" class="max-w-4xl">
          <h2 class="text-lg font-semibold text-n-slate-12">
            Saída de informações
          </h2>

          <p class="mt-1 text-sm text-n-slate-10">
            Controle como os dados descem da Central para outros destinos.
          </p>

          <div class="mt-6 rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <label class="text-sm font-medium text-n-slate-12">
              Resposta pública
            </label>

            <select
              v-model="configuration.public_reply_transport"
              class="mt-2 block h-10 w-full max-w-sm rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            >
              <option value="email">Enviar por e-mail</option>

              <option value="none">Apenas registrar no Ticket</option>
            </select>
          </div>

          <div class="mt-4 space-y-3">
            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Notas privadas
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Permanecem somente dentro do Ticket.
                </p>
              </div>

              <input
                v-model="configuration.private_notes_enabled"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>

            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Avisar ao criar Ticket
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Notifica o solicitante após a abertura.
                </p>
              </div>

              <input
                v-model="configuration.notify_requester_on_create"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>

            <label
              class="flex items-start justify-between gap-5 rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Avisar em mudança de status
                </p>

                <p class="mt-1 text-xs text-n-slate-9">
                  Notifica quando o status do Ticket mudar.
                </p>
              </div>

              <input
                v-model="configuration.notify_requester_on_status_change"
                type="checkbox"
                class="mt-1 size-4"
              />
            </label>
          </div>

          <button
            type="button"
            class="mt-6 inline-flex h-10 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white"
            @click="saveConfiguration"
          >
            <span class="i-lucide-save size-4" />
            Salvar saída
          </button>
        </section>

        <!-- EMAIL POR DEPARTAMENTO -->

        <DepartmentMailSettings v-else-if="activeTab === 'departments'" />

        <!-- INTEGRACOES -->
        <section v-else-if="activeTab === 'integrations'" class="max-w-5xl">
          <h2 class="text-lg font-semibold text-n-slate-12">Integrações</h2>

          <p class="mt-1 text-sm text-n-slate-10">
            Adaptadores de entrada e saída da Central.
          </p>

          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <article
              v-for="integration in integrations"
              :key="integration.id"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <div class="flex items-start gap-3">
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <span
                    class="size-5"
                    :class="integrationIcon(integration.key)"
                  />
                </span>

                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <h3 class="text-sm font-semibold text-n-slate-12">
                        {{ integration.name }}
                      </h3>

                      <p class="mt-1 text-xs text-n-slate-9">
                        {{ integrationDirection(integration.direction) }}
                      </p>
                    </div>

                    <span
                      class="rounded-full border border-n-weak px-2 py-1 text-[10px] text-n-slate-10"
                    >
                      {{ integrationStatus(integration.status) }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                class="mt-4 border-t border-n-weak pt-4 text-xs text-n-slate-9"
              >
                <template v-if="integration.key === 'api'">
                  Controle de entrada disponível na aba
                  <strong>Entrada</strong>.
                </template>

                <template v-else-if="integration.key === 'conversation_import'">
                  Importa uma cópia do atendimento. O Ticket permanece
                  independente depois disso.
                </template>

                <template v-else-if="integration.key === 'email'">
                  SMTP, remetente e recebimento de respostas serão configurados
                  na etapa de transporte.
                </template>

                <template v-else-if="integration.key === 'webhook'">
                  URLs, eventos e assinatura serão configurados na etapa de
                  transporte.
                </template>
              </div>
            </article>
          </div>
        </section>

        <!-- STATUS -->
        <section v-else-if="activeTab === 'statuses'" class="max-w-5xl">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">Status</h2>

              <p class="mt-1 text-sm text-n-slate-10">
                Fluxo operacional próprio dos Tickets.
              </p>
            </div>

            <button
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
              @click="newStatus"
            >
              <span class="i-lucide-plus size-4" />
              Novo status
            </button>
          </div>

          <div class="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div
              class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
            >
              <button
                v-for="status in statuses"
                :key="status.id"
                type="button"
                class="flex w-full items-center gap-3 border-b border-n-weak px-4 py-3 text-left last:border-b-0 hover:bg-n-alpha-1"
                @click="editStatus(status)"
              >
                <span
                  class="size-3 shrink-0 rounded-full"
                  :style="{
                    backgroundColor: status.color || '#7C3AED',
                  }"
                />

                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ status.name }}
                  </p>

                  <p class="text-xs text-n-slate-9">
                    {{ stateGroupLabel(status.state_group) }}
                  </p>
                </div>

                <span
                  v-if="status.default_status"
                  class="rounded-full bg-n-brand/10 px-2 py-1 text-[10px] font-medium text-n-brand"
                >
                  Padrão
                </span>

                <span
                  v-if="status.terminal"
                  class="rounded-full border border-n-weak px-2 py-1 text-[10px] text-n-slate-9"
                >
                  Terminal
                </span>
              </button>
            </div>

            <div
              v-if="statusDraft"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ statusDraft.id ? 'Editar status' : 'Novo status' }}
              </h3>

              <div class="mt-4 space-y-4">
                <div>
                  <label class="text-xs text-n-slate-9"> Nome </label>

                  <input
                    v-model="statusDraft.name"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  />
                </div>

                <div>
                  <label class="text-xs text-n-slate-9"> Slug </label>

                  <input
                    v-model="statusDraft.slug"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  />
                </div>

                <div>
                  <label class="text-xs text-n-slate-9">
                    Grupo operacional
                  </label>

                  <select
                    v-model="statusDraft.state_group"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  >
                    <option
                      v-for="group in stateGroups"
                      :key="group.value"
                      :value="group.value"
                    >
                      {{ group.label }}
                    </option>
                  </select>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="text-xs text-n-slate-9"> Cor </label>

                    <input
                      v-model="statusDraft.color"
                      type="color"
                      class="mt-1 h-9 w-full rounded-lg border border-n-weak"
                    />
                  </div>

                  <div>
                    <label class="text-xs text-n-slate-9"> Ordem </label>

                    <input
                      v-model.number="statusDraft.position"
                      type="number"
                      min="0"
                      class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                    />
                  </div>
                </div>

                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input v-model="statusDraft.active" type="checkbox" />
                  Ativo
                </label>

                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input v-model="statusDraft.default_status" type="checkbox" />
                  Padrão
                </label>

                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input v-model="statusDraft.terminal" type="checkbox" />
                  Terminal
                </label>
              </div>

              <div class="mt-5 flex gap-2">
                <button
                  type="button"
                  class="h-9 flex-1 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
                  :disabled="savingStatus"
                  @click="saveStatus"
                >
                  Salvar
                </button>

                <button
                  v-if="statusDraft.id"
                  type="button"
                  class="h-9 rounded-lg border border-n-weak px-3 text-sm text-red-600"
                  @click="deleteStatus(statusDraft)"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- CATEGORIAS -->
        <section v-else-if="activeTab === 'categories'" class="max-w-5xl">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">Categorias</h2>

              <p class="mt-1 text-sm text-n-slate-10">
                Categorias e subcategorias próprias dos Tickets.
              </p>
            </div>

            <button
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
              @click="newCategory"
            >
              <span class="i-lucide-plus size-4" />
              Nova categoria
            </button>
          </div>

          <div class="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-2">
              <div
                v-for="category in rootCategories"
                :key="category.id"
                class="mb-1 last:mb-0"
              >
                <button
                  type="button"
                  class="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-n-alpha-1"
                  @click="editCategory(category)"
                >
                  <span
                    class="size-3 rounded-full"
                    :style="{
                      backgroundColor: category.color || '#7C3AED',
                    }"
                  />

                  <span class="flex-1 text-sm font-medium text-n-slate-12">
                    {{ category.name }}
                  </span>
                </button>

                <button
                  v-for="child in childrenOf(category.id)"
                  :key="child.id"
                  type="button"
                  class="ml-7 flex items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-1"
                  @click="editCategory(child)"
                >
                  <span
                    class="i-lucide-corner-down-right size-3.5 text-n-slate-8"
                  />

                  <span
                    class="size-2.5 rounded-full"
                    :style="{
                      backgroundColor: child.color || '#7C3AED',
                    }"
                  />

                  <span class="text-sm text-n-slate-11">
                    {{ child.name }}
                  </span>
                </button>
              </div>
            </div>

            <div
              v-if="categoryDraft"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ categoryDraft.id ? 'Editar categoria' : 'Nova categoria' }}
              </h3>

              <div class="mt-4 space-y-4">
                <div>
                  <label class="text-xs text-n-slate-9"> Nome </label>

                  <input
                    v-model="categoryDraft.name"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  />
                </div>

                <div>
                  <label class="text-xs text-n-slate-9"> Slug </label>

                  <input
                    v-model="categoryDraft.slug"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  />
                </div>

                <div>
                  <label class="text-xs text-n-slate-9">
                    Categoria principal
                  </label>

                  <select
                    v-model="categoryDraft.parent_id"
                    class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                  >
                    <option :value="null">Nenhuma</option>

                    <option
                      v-for="root in rootCategories"
                      :key="root.id"
                      :value="root.id"
                      :disabled="Number(root.id) === Number(categoryDraft.id)"
                    >
                      {{ root.name }}
                    </option>
                  </select>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="text-xs text-n-slate-9"> Cor </label>

                    <input
                      v-model="categoryDraft.color"
                      type="color"
                      class="mt-1 h-9 w-full rounded-lg border border-n-weak"
                    />
                  </div>

                  <div>
                    <label class="text-xs text-n-slate-9"> Ordem </label>

                    <input
                      v-model.number="categoryDraft.position"
                      type="number"
                      min="0"
                      class="mt-1 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                    />
                  </div>
                </div>

                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input v-model="categoryDraft.active" type="checkbox" />
                  Ativa
                </label>
              </div>

              <div class="mt-5 flex gap-2">
                <button
                  type="button"
                  class="h-9 flex-1 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
                  :disabled="savingCategory"
                  @click="saveCategory"
                >
                  Salvar
                </button>

                <button
                  v-if="categoryDraft.id"
                  type="button"
                  class="h-9 rounded-lg border border-n-weak px-3 text-sm text-red-600"
                  @click="deleteCategory(categoryDraft)"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>
